/* ── dashboard atoms ────────────────────────────────────────── */
import Icon3D, { FeedIcon } from '../Icons3D';
import { Num, countMetric } from './numbers';

export function Kpi({ label, value, delta, metricKey, entity, type, spark }) {
  return (
    <div className="card kpi">
      <h5>{label}</h5>
      <div className="v">
        <Num
          value={value}
          label={label}
          metricKey={metricKey ?? countMetric(label)}
          entity={entity}
          type={type}
        />
      </div>
      {delta && <div className="d">{delta}</div>}
      {spark}
    </div>
  );
}

/** Sparkline drawn from a plain series — the KPI cards' trend line. */
export function Sparkline({ series, width = 86, height = 34, stroke = 'var(--green)' }) {
  if (!series || series.length < 2) return null;
  const min = Math.min(...series);
  const max = Math.max(...series);
  const span = max - min || 1;
  const points = series
    .map((v, i) => `${(i / (series.length - 1)) * width},${height - 4 - ((v - min) / span) * (height - 8)}`)
    .join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <polyline points={points} fill="none" stroke={stroke} strokeWidth="2" />
    </svg>
  );
}

/** Column sparkline (net flows KPI). */
export function SparkBars({ series, width = 86, height = 34 }) {
  if (!series || !series.length) return null;
  const max = Math.max(...series.map(Math.abs)) || 1;
  const bw = width / series.length - 4;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      {series.map((v, i) => {
        const h = Math.max(2, (Math.abs(v) / max) * (height - 6));
        return (
          <rect
            key={i}
            x={i * (bw + 4) + 2}
            y={height - h}
            width={bw}
            height={h}
            rx="2"
            fill={i >= series.length - 4 ? 'var(--brand)' : 'color-mix(in srgb,var(--brand) 30%,var(--panel))'}
          />
        );
      })}
    </svg>
  );
}

export function AiStrip({ title, children, chips, actions }) {
  return (
    <div className="ai-strip">
      <div className="ai-orb ic3fi"><Icon3D name="spark" /></div>
      <div style={{ flex: 1 }}>
        <b>{title}</b>
        <p>{children}</p>
        {chips && (
          <div style={{ display: 'flex', gap: 7, marginTop: 9, flexWrap: 'wrap' }}>{chips}</div>
        )}
      </div>
      {actions && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 'none', alignSelf: 'center' }}>
          {actions}
        </div>
      )}
    </div>
  );
}

export function Feed({ children }) {
  return <ul className="feed">{children}</ul>;
}

export function FeedItem({ icon, title, body, actions, time, compact }) {
  return (
    <li style={compact ? { padding: '10px 16px' } : undefined}>
      <FeedIcon name={icon} />
      <div>
        <b style={compact ? { fontSize: '12.3px' } : undefined}>{title}</b>
        {body && <p style={compact ? { fontSize: '11.3px' } : undefined}>{body}</p>}
        {actions && <div className="act">{actions}</div>}
      </div>
      {time && <time>{time}</time>}
    </li>
  );
}

/**
 * One line of what needs doing.
 *
 * The checkbox appears only when there is something to tick: these items are
 * computed from live data (a drift, a goal behind plan, a failing SIP), not
 * stored, and nothing on the backend can mark one done — `createActionItem`
 * and friends exist but resolve the owner from the token, so a desk filing one
 * for a client would file it against itself. A box that cannot be checked
 * reads as a task list that has lost its state, so without `onToggle` the row
 * is marked rather than checkable.
 */
export function Task({ children, meta, onToggle, done = false }) {
  return (
    <div className="task">
      {onToggle ? (
        <div
          className="cbx"
          role="checkbox"
          aria-checked={done}
          tabIndex={0}
          onClick={onToggle}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        />
      ) : (
        <span
          aria-hidden="true"
          style={{
            width: 6, height: 6, borderRadius: '50%', background: 'var(--ink3)',
            flex: 'none', marginTop: 7, opacity: 0.55,
          }}
        />
      )}
      <p>{children}</p>
      {meta ?? <span />}
    </div>
  );
}

export function StatStrip({ stats, entity }) {
  return (
    <div className="stat-strip">
      {stats.map((s) => (
        <div key={s.label}>
          <h6>{s.label}</h6>
          <b className={s.tone || ''}>
            <Num
              value={s.value}
              label={s.label}
              metricKey={s.metricKey ?? countMetric(s.label)}
              entity={s.entity ?? entity}
              type={s.type}
            />
          </b>
          <span className={s.subTone || ''} style={s.subTone ? undefined : { color: 'var(--ink3)' }}>
            {s.sub}
          </span>
        </div>
      ))}
    </div>
  );
}
